import { Injectable } from '@angular/core';
import { NbDialogService } from '@nebular/theme';
import { catchError, map, throwError } from 'rxjs';
import { resourceTypes } from '@shared/constants/resourceTypes';
import { CameraPreviewModalComponent } from '@shared/modules/camera-preview-shared/modals/camera-preview-modal/camera-preview-modal.component';
import { ShareDeviceModalComponent } from '../modals/share-device-modal/share-device-modal.component';
import { UnavailableFindDeviceModalComponent } from '@shared/modules/account/modals/unavailable-find-device-modal/unavailable-find-device-modal.component';
import { UnavailableTeamsModalComponent } from '@shared/modules/account/modals/unavailable-teams-modal/unavailable-teams-modal.component';
import { AlertService } from '@shared/services/alert.service';
import { ApiService } from '@shared/services/api.service';
import { StorageService } from '@shared/services/storage.service';
import { UserService } from '@shared/modules/account/services/user.service';
import { AccessService } from '@access/access.service';
import { PERMISSIONS } from '@access/permissions';

@Injectable({
  providedIn: 'root'
})
export class DeviceService {

  resourceTypes = resourceTypes;

  constructor(
    private api: ApiService,
    private alertService: AlertService,
    private userService: UserService,
    private dialogService: NbDialogService,
    private storageService: StorageService,
    private readonly accessService: AccessService
  ) { }

  shareDevice(deviceId: string): void {
    if (this.accessService.hasAll([PERMISSIONS.Teams.View, PERMISSIONS.Teams.Manage])) {
      this.dialogService.open<any>(ShareDeviceModalComponent,
        {
          context: { deviceId: deviceId }
        });
    } else {
      this.dialogService.open<any>(UnavailableTeamsModalComponent, {});
    }
  }

  findDevice(deviceId: string, isLocalised: boolean) {
    if (!this.accessService.hasPermission(PERMISSIONS.Devices.Commands.Find)) {
      this.dialogService.open<any>(UnavailableFindDeviceModalComponent, {});
      return;
    }
    
    this.api.postDeviceFindMethod(deviceId, !isLocalised)
      .subscribe({
        error: (error: { error: number }) => {},
        next: () => {
          const message = isLocalised ? 'commands.localization-off-success' : 'commands.localization-on-success';
          this.alertService.success(message);
        }
      });
  }

  openCameraPreviewModal(deviceId: string, deviceName: string, supportLiveStream: boolean) {
    this.dialogService.open<any>(CameraPreviewModalComponent, {
      context: { deviceId: deviceId, deviceName: deviceName, supportLiveStream: supportLiveStream },
    });
  }

  startCamera(deviceId: string) {
    return this.api.postCameraStart(deviceId)
      .pipe(
        catchError(err => {
          this.alertService.danger( err.error)
          return throwError(() => err);
        }),
        map(res => {
          this.storageService.setSessionStorage(deviceId, res.id);
          return res;
        })
      );
  }

  stopCamera(deviceId: string) {
    const streamId = this.storageService.getSessionStorage(deviceId);
    if (!streamId) return;

    return this.api.postCameraStop(deviceId, streamId)
      .pipe(
        catchError(err => {
          this.alertService.danger( err.error)
          return throwError(() => err);
        })
      );
  }

  refreshCamera(deviceId: string) {
    const streamId = this.storageService.getSessionStorage(deviceId);
    if (!streamId) return;

    return this.api.postCameraRefresh(deviceId, streamId).pipe(catchError(err => {
      this.alertService.danger( err.error)
      return throwError(() => err);
    }), map(_ => {
      return streamId;
    }))
  }
}
